import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Eye, EyeOff, ArrowLeft, ShieldCheck, Lock, ArrowRight } from 'lucide-react';
import { auth, ApiError } from '@/api/client';
import { PasswordStrengthMeter } from '@/components/PasswordStrengthMeter';
import { AuthShell, HipaaBadge, AuthErrorBanner } from '@/components/auth/AuthShell';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const tokenId = params.get('tid') ?? '';
  const token   = params.get('t')   ?? '';

  const [password, setPassword]         = useState('');
  const [confirm, setConfirm]           = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [policyMet, setPolicyMet]       = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [success, setSuccess]           = useState(false);

  useEffect(() => {
    if (!tokenId || !token) setError('This reset link is invalid or incomplete.');
  }, [tokenId, token]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setSubmitting(true);
    try {
      await auth.resetPassword(tokenId, token, password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'RATE_LIMITED') {
          setError('Too many attempts. Please wait before trying again.');
        } else if (err.code === 'VALIDATION_ERROR') {
          const details = err.details as { errors?: string[] } | null;
          const policyErrors = details?.errors;
          setError(policyErrors?.length ? policyErrors.join(' ') : err.message);
        } else {
          setError(err.message || 'Password reset failed. Please try again.');
        }
      } else {
        setError('Password reset failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // `setup=1` marks a platform-user account-setup link (13 Oct 2026 emails).
  // First-time users get the Welcome / Activate account screen, not a reset.
  if (params.get('setup') === '1' && tokenId && token) {
    return <Navigate replace to={`/auth/accept-invite?tid=${encodeURIComponent(tokenId)}&t=${encodeURIComponent(token)}`} />;
  }

  return (
    <AuthShell
      title={<>Reset your<br/>password</>}
      lede="Create a strong new password to regain secure access to your account."
    >
      {success ? (
        <div className="text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-navy-50 grid place-items-center">
            <ShieldCheck size={24} className="text-navy"/>
          </div>
          <p className="eyebrow mb-2">Success</p>
          <h1 className="text-[24px] font-extrabold text-text-1 mb-2 tracking-tight">Password updated</h1>
          <p className="text-[12.5px] text-text-3 leading-relaxed">
            Your password has been changed. Redirecting to sign in…
          </p>
        </div>
      ) : (
        <div className="pt-8">
          <p className="eyebrow mb-2">Password reset</p>
          <h1 className="text-[26px] font-extrabold text-text-1 mb-1.5 tracking-tight">Set a new password</h1>
          <p className="text-[12.5px] text-text-3 mb-6 leading-relaxed">
            Minimum 12 characters. Don't reuse a previous password.
          </p>

          <div className="relative bg-white border border-border rounded-sm p-7 pb-6 shadow-card">
            <HipaaBadge />
            <form onSubmit={handleSubmit} noValidate>
              <div className="mb-1.5">
                <label htmlFor="password" className="label text-[10px] tracking-wider mb-2">New Password</label>
                <div className="flex items-center h-[46px] gap-2.5 px-3.5 bg-white border border-border rounded-sm transition-colors focus-within:border-navy">
                  <Lock size={15} className="text-text-4 shrink-0"/>
                  <input
                    id="password" type={showPassword ? 'text' : 'password'} value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Min. 12 characters"
                    autoComplete="new-password" required minLength={12} maxLength={20}
                    className="flex-1 bg-transparent border-0 outline-none text-[13.5px] text-text-1 placeholder:text-text-4"
                  />
                  <button
                    type="button" onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="p-1 text-text-4 hover:text-text-2"
                  >
                    {showPassword ? <EyeOff size={15}/> : <Eye size={15}/>}
                  </button>
                </div>
              </div>

              <div className="mb-[18px]">
                <PasswordStrengthMeter password={password} onPolicyChange={setPolicyMet} />
              </div>

              <div className="mb-[18px]">
                <label htmlFor="confirm" className="label text-[10px] tracking-wider mb-2">Confirm Password</label>
                <div className="flex items-center h-[46px] gap-2.5 px-3.5 bg-white border border-border rounded-sm transition-colors focus-within:border-navy">
                  <Lock size={15} className="text-text-4 shrink-0"/>
                  <input
                    id="confirm" type={showPassword ? 'text' : 'password'} value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    placeholder="Repeat password"
                    autoComplete="new-password" required minLength={12} maxLength={20}
                    className="flex-1 bg-transparent border-0 outline-none text-[13.5px] text-text-1 placeholder:text-text-4"
                  />
                </div>
              </div>

              <div className="h-[46px] flex items-center mb-3.5">
                {error && <AuthErrorBanner message={error} />}
              </div>

              <button
                type="submit" disabled={submitting || !tokenId || !token || !policyMet || password !== confirm}
                className="w-full h-12 bg-navy hover:bg-navy-600 active:bg-navy-700 text-white font-bold text-[13.5px] tracking-wide rounded-sm flex items-center justify-center gap-1.5 transition-colors shadow-card disabled:opacity-65 disabled:cursor-not-allowed mb-4"
              >
                {submitting
                  ? <><Loader2 size={14} className="animate-spin"/> Updating…</>
                  : <>Update password <ArrowRight size={14} className="opacity-75" /></>}
              </button>

              <div className="text-center">
                <Link to="/login" className="inline-flex items-center gap-1.5 text-[12px] font-bold text-navy hover:underline">
                  <ArrowLeft size={13}/> Back to sign in
                </Link>
              </div>
            </form>
          </div>
        </div>
      )}
    </AuthShell>
  );
}
